// src/Pages/User.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../utils/api";
import "../css/user.css";

export default function User() {
  const navigate = useNavigate();

  // --- JWT 파서 유틸 (base64url → json)
  function parseJwt(token) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  function isAdminToken(token) {
    const payload = parseJwt(token);
    const role = payload?.role;
    const roles = payload?.roles;
    const authorities = payload?.authorities;
    const scope = payload?.scope;
    if (role && String(role).toUpperCase() === "ADMIN") return true;
    if (Array.isArray(roles) && roles.map(String).some(r => r.toUpperCase() === "ADMIN")) return true;
    if (Array.isArray(authorities) && authorities.map(String).some(r => r.toUpperCase().includes("ADMIN"))) return true;
    if (typeof scope === "string" && scope.split(" ").some(s => s.toUpperCase().includes("ADMIN"))) return true;
    return false;
  }

  // --- 진입 가드: 토큰 없으면 로그인, ADMIN 아니면 홈으로
  useEffect(() => {
    const t = localStorage.getItem("accessToken");
    if (!t) {
      alert("로그인이 필요합니다.");
      navigate("/login");
      return;
    }
    if (!isAdminToken(t)) {
      alert("접근 권한이 없습니다. (ADMIN 전용)");
      navigate("/");
    }
  }, [navigate]);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  const reload = useCallback(async (signal) => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/users", { signal });
      setUsers(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      // 취소는 무시
      if (err?.name === "CanceledError" || err?.message === "canceled") return;
      const msg = err?.response?.data?.message || err?.message || "목록을 불러오지 못했습니다.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    return () => controller.abort();
  }, [reload]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u?.name, u?.username, u?.email, u?.role, ...(u?.roles ?? [])]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [users, query]);

  return (
    <div className="user-container">
      <div className="user-header">
        <h2 className="user-title">사용자 관리</h2>
        <div className="user-controls">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="이름/역할 검색"
            className="input input--search"
          />
          <button onClick={() => reload()} className="btn">새로고침</button>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="status status--muted animate-pulse">목록을 불러오는 중…</div>
        ) : error ? (
          <div className="status status--error">오류: {error}</div>
        ) : filtered.length === 0 ? (
          <div className="status status--muted">표시할 사용자가 없습니다.</div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>이름</th>
                  <th>역할</th>
                  <th className="col-narrow">상세</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const id = normalizeId(u);
                  return (
                    <tr key={id}>
                      <td>{id}</td>
                      <td>{u?.name ?? u?.username ?? "-"}</td>
                      <td>{u?.role ?? (u?.roles?.join?.(", ") ?? "-")}</td>
                      <td>
                        <Link
                          to={`/users/${encodeURIComponent(id)}`}
                          className="btn"
                          style={{ textDecoration: "none", color: "white" }}
                        >
                          보기
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function normalizeId(u) {
  return u?.id ?? u?.userId ?? u?.username ?? u?.email;
}
